import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  Row,
  Col,
  Input,
  Table,
  Space,
  Tooltip,
  Select,
  Tag,
  Modal,
  Descriptions,
  message
} from 'antd';
import {
  EyeOutlined,
  ReloadOutlined,
  CheckOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { contactRequestsService, ContactRequest } from '../services/contactRequestsService';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socketService';
import { exportToCSV } from '../utils/exportUtils';

const statusMap = {
  pending: { label: 'En attente', color: 'orange' },
  in_progress: { label: 'En cours', color: 'blue' },
  resolved: { label: 'Résolu', color: 'green' }
} as const;

const urgencyMap = {
  low: { label: 'Basse', color: 'blue' },
  medium: { label: 'Moyenne', color: 'orange' },
  high: { label: 'Haute', color: 'volcano' },
  critical: { label: 'Critique', color: 'red' }
} as const;

const motiveMap = {
  entretien: 'Entretien',
  reclamation: 'Réclamation',
  information_commerciale: 'Information Commerciale',
  autre: 'Autre'
} as const;

const channelMap = {
  phone: 'Téléphone',
  email: 'E-mail',
  chat: 'Chat'
} as const;

const timeMap = {
  morning: 'Matin',
  afternoon: 'Après-midi'
} as const;

const ContactRequestsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';

  const [requests, setRequests] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [motiveFilter, setMotiveFilter] = useState<string>('');
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // État du Modal de résolution obligatoire
  const [resolutionModalOpen, setResolutionModalOpen] = useState<boolean>(false);
  const [targetResolveId, setTargetResolveId] = useState<number | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState<string>('');
  const [submittingResolution, setSubmittingResolution] = useState<boolean>(false);

  const { currentUser } = useAuth();
  const isAdminOrTech = currentUser?.role === 'admin' || currentUser?.role === 'technician';

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contactRequestsService.getContactRequests({
        status: statusFilter,
        motive: motiveFilter,
        limit: 1000
      });
      setRequests(response.data || []);
    } catch (error) {
      message.error('Erreur lors du chargement des demandes de contact');
      console.error('Error fetching contact requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, motiveFilter]);

  useEffect(() => {
    fetchRequests();

    const handleNewContactRequest = (data: any) => {
      message.info(`📞 Nouvelle demande de rappel reçue (${data.name || 'Client'})`);
      fetchRequests();
    };

    socketService.on('new_contact_request', handleNewContactRequest);
    socketService.on('contact_request_created', handleNewContactRequest);

    return () => {
      socketService.off('new_contact_request', handleNewContactRequest);
      socketService.off('contact_request_created', handleNewContactRequest);
    };
  }, [fetchRequests]);

  const handleUpdateStatus = async (id: number, status: string) => {
    if (status === 'resolved') {
      // Exiger le commentaire via le modal dédié
      setTargetResolveId(id);
      setResolutionNotes('');
      setResolutionModalOpen(true);
      return;
    }

    try {
      setUpdatingId(id);
      await contactRequestsService.updateContactRequest(id, { status });
      message.success('Statut de la demande mis à jour');
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest(prev => prev ? { ...prev, status: status as any } : null);
      }
      fetchRequests();
    } catch (error) {
      message.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleConfirmResolution = async () => {
    if (!targetResolveId) return;
    const trimmedNotes = resolutionNotes.trim();
    if (!trimmedNotes) {
      message.error('Le commentaire de résolution expliquant l’action réalisée est obligatoire.');
      return;
    }

    try {
      setSubmittingResolution(true);
      await contactRequestsService.updateContactRequest(targetResolveId, {
        status: 'resolved',
        notes: trimmedNotes
      });
      message.success('Demande de rappel clôturée avec succès !');
      if (selectedRequest && selectedRequest.id === targetResolveId) {
        setSelectedRequest(prev => prev ? { ...prev, status: 'resolved', resolution_notes: trimmedNotes } : null);
      }
      setResolutionModalOpen(false);
      setTargetResolveId(null);
      setResolutionNotes('');
      fetchRequests();
    } catch (error: any) {
      console.error('Erreur résolution rappel:', error);
      message.error(error.response?.data?.error || 'Erreur lors de la clôture de la demande');
    } finally {
      setSubmittingResolution(false);
    }
  };

  const handleUpdateUrgency = async (id: number, urgencyLevel: string) => {
    try {
      setUpdatingId(id);
      await contactRequestsService.updateContactRequest(id, { urgencyLevel });
      message.success("Niveau d'urgence mis à jour");
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest(prev => prev ? { ...prev, urgency_level: urgencyLevel as any } : null);
      }
      fetchRequests();
    } catch (error) {
      message.error("Erreur lors de la mise à jour de l'urgence");
    } finally {
      setUpdatingId(null);
    }
  };

  const showDetails = (request: ContactRequest) => {
    setSelectedRequest(request);
    setModalVisible(true);
  };

  const filteredRequests = requests.filter((req) =>
    req.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.phone.includes(searchTerm) ||
    req.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (req.email && req.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const columns = [
    {
      title: 'Nom & Prénoms',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'Motif',
      dataIndex: 'motive',
      key: 'motive',
      render: (motive: keyof typeof motiveMap) => motiveMap[motive] || motive
    },
    {
      title: 'Canal',
      dataIndex: 'preferred_channel',
      key: 'preferred_channel',
      render: (channel: keyof typeof channelMap) => channelMap[channel] || channel
    },
    {
      title: 'Créneau',
      dataIndex: 'preferred_time',
      key: 'preferred_time',
      render: (time: keyof typeof timeMap) => timeMap[time] || time
    },
    {
      title: 'Urgence',
      dataIndex: 'urgency_level',
      key: 'urgency_level',
      render: (urgency: keyof typeof urgencyMap, record: ContactRequest) => (
        <Select
          value={urgency}
          disabled={!isAdminOrTech || updatingId === record.id}
          onChange={(val) => handleUpdateUrgency(record.id, val)}
          style={{ width: 120 }}
          bordered={false}
        >
          {Object.entries(urgencyMap).map(([key, val]) => (
            <Select.Option key={key} value={key}>
              <Tag color={val.color}>{val.label}</Tag>
            </Select.Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: keyof typeof statusMap, record: ContactRequest) => (
        <Select
          value={status}
          disabled={!isAdminOrTech || updatingId === record.id}
          onChange={(val) => handleUpdateStatus(record.id, val)}
          style={{ width: 130 }}
          bordered={false}
        >
          {Object.entries(statusMap).map(([key, val]) => (
            <Select.Option key={key} value={key}>
              <Tag color={val.color}>{val.label}</Tag>
            </Select.Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Date de réception',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ContactRequest) => (
        <Space size="middle">
          <Tooltip title="Voir les détails">
            <Button
              type="primary"
              shape="circle"
              icon={<EyeOutlined />}
              onClick={() => showDetails(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const [exporting, setExporting] = useState<boolean>(false);

  const handleExport = () => {
    try {
      setExporting(true);
      const rows = filteredRequests.map(r => ({
        id: r.id,
        nom: r.name,
        telephone: r.phone,
        email: r.email || '',
        motif: motiveMap[r.motive as keyof typeof motiveMap] || r.motive,
        canal_souhaite: channelMap[r.preferred_channel as keyof typeof channelMap] || r.preferred_channel,
        creneau_joignabilite: timeMap[r.preferred_time as keyof typeof timeMap] || r.preferred_time,
        urgence: urgencyMap[r.urgency_level as keyof typeof urgencyMap]?.label || r.urgency_level,
        statut: statusMap[r.status as keyof typeof statusMap]?.label || r.status,
        date_demande: r.created_at ? dayjs(r.created_at).format('DD/MM/YYYY HH:mm') : '',
        message: r.message || '',
        notes_resolution: r.resolution_notes || ''
      }));

      const headers = [
        { header: 'ID', key: 'id' },
        { header: 'Nom & Prénoms', key: 'nom' },
        { header: 'Téléphone', key: 'telephone' },
        { header: 'Email', key: 'email' },
        { header: 'Motif', key: 'motif' },
        { header: 'Canal Souhaité', key: 'canal_souhaite' },
        { header: 'Créneau de Joignabilité', key: 'creneau_joignabilite' },
        { header: 'Urgence', key: 'urgence' },
        { header: 'Statut', key: 'statut' },
        { header: 'Date de Demande', key: 'date_demande' },
        { header: 'Message Client', key: 'message' },
        { header: 'Notes de Résolution', key: 'notes_resolution' },
      ];

      exportToCSV(rows, headers, `demandes_rappel_${new Date().toISOString().slice(0, 10)}.csv`);
      message.success('Export des demandes de rappel généré avec succès');
    } catch (err) {
      console.error('Erreur export rappels:', err);
      message.error("Erreur lors de l'export des demandes de rappel");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <h2>Demandes de rappel (Hors-horaires)</h2>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exporting}
              >
                Exporter
              </Button>
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={fetchRequests}
                loading={loading}
              >
                Actualiser
              </Button>
            </Space>
          </Col>
        </Row>
        
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={8}>
            <Input.Search
              placeholder="Rechercher par nom, tél, message..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Select
              placeholder="Filtrer par statut (Toutes par défaut)"
              style={{ width: '100%' }}
              allowClear
              value={statusFilter || undefined}
              onChange={(val) => setStatusFilter(val || '')}
            >
              <Select.Option value="">Toutes les demandes</Select.Option>
              {Object.entries(statusMap).map(([key, val]) => (
                <Select.Option key={key} value={key}>{val.label}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={8}>
            <Select
              placeholder="Filtrer par motif"
              style={{ width: '100%' }}
              allowClear
              value={motiveFilter || undefined}
              onChange={(val) => setMotiveFilter(val || '')}
            >
              {Object.entries(motiveMap).map(([key, val]) => (
                <Select.Option key={key} value={key}>{val}</Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          dataSource={filteredRequests}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Modal de Détails */}
      <Modal
        title="Détails de la demande de rappel"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Fermer
          </Button>
        ]}
        width={700}
      >
        {selectedRequest && (
          <Descriptions bordered column={2} size="middle" layout="vertical">
            <Descriptions.Item label="Nom et prénoms" span={2}>
              <strong>{selectedRequest.name}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Numéro de téléphone">
              {selectedRequest.phone}
            </Descriptions.Item>
            <Descriptions.Item label="Adresse e-mail">
              {selectedRequest.email || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Motif de contact">
              <Tag color="cyan">{motiveMap[selectedRequest.motive] || selectedRequest.motive}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Canal de rappel">
              {channelMap[selectedRequest.preferred_channel] || selectedRequest.preferred_channel}
            </Descriptions.Item>
            <Descriptions.Item label="Créneau de rappel">
              {timeMap[selectedRequest.preferred_time] || selectedRequest.preferred_time}
            </Descriptions.Item>
            <Descriptions.Item label="Niveau d'urgence">
              <Select
                value={selectedRequest.urgency_level}
                disabled={!isAdminOrTech}
                onChange={(val) => handleUpdateUrgency(selectedRequest.id, val)}
                style={{ width: 120 }}
              >
                {Object.entries(urgencyMap).map(([key, val]) => (
                  <Select.Option key={key} value={key}>
                    <Tag color={val.color}>{val.label}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Descriptions.Item>
            <Descriptions.Item label="Statut de la demande">
              <Select
                value={selectedRequest.status}
                disabled={!isAdminOrTech}
                onChange={(val) => handleUpdateStatus(selectedRequest.id, val)}
                style={{ width: 130 }}
              >
                {Object.entries(statusMap).map(([key, val]) => (
                  <Select.Option key={key} value={key}>
                    <Tag color={val.color}>{val.label}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Descriptions.Item>
            <Descriptions.Item label="Date de soumission">
              {dayjs(selectedRequest.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            {selectedRequest.customer && (
              <Descriptions.Item label="Profil Client associé" span={2}>
                {selectedRequest.customer.first_name} {selectedRequest.customer.last_name} (ID: {selectedRequest.customer.id})
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Description détaillée du problème ou de la question" span={2}>
              <div style={{ whiteSpace: 'pre-wrap', background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                {selectedRequest.message}
              </div>
            </Descriptions.Item>
            {selectedRequest.resolution_notes && (
              <Descriptions.Item label="Commentaire de résolution (Action réalisée)" span={2}>
                <div style={{ whiteSpace: 'pre-wrap', background: '#f0fdf4', color: '#15803d', padding: 12, borderRadius: 8, border: '1px solid #86efac', fontWeight: 500 }}>
                  {selectedRequest.resolution_notes}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* MODAL COMMENTAIRE DE RÉSOLUTION OBLIGATOIRE */}
      <Modal
        title={
          <Space>
            <CheckOutlined style={{ color: '#16a34a' }} />
            <span>Commentaire de résolution obligatoire</span>
          </Space>
        }
        open={resolutionModalOpen}
        onCancel={() => {
          if (!submittingResolution) {
            setResolutionModalOpen(false);
            setTargetResolveId(null);
            setResolutionNotes('');
          }
        }}
        onOk={handleConfirmResolution}
        confirmLoading={submittingResolution}
        okText="Valider et Clore"
        cancelText="Annuler"
        okButtonProps={{ style: { backgroundColor: '#16a34a', borderColor: '#16a34a', fontWeight: 600 } }}
      >
        <div style={{ marginBottom: 12, color: '#475569', fontSize: 13 }}>
          Veuillez saisir un commentaire obligatoire décrivant le résultat de l'échange ou l'action réalisée avant de clore cette demande de rappel :
        </div>
        <Input.TextArea
          rows={4}
          placeholder="Ex: Client contacté par téléphone. Explications fournies concernant le contrat d'entretien."
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ContactRequestsPage;
